import { Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Cross-tab session synchronization service
 * 
 * This service provides cross-tab communication to synchronize authentication state
 * across multiple browser tabs/windows. It uses BroadcastChannel API for modern browsers
 * and falls back to localStorage events for older browsers.
 * 
 * Key Features:
 * - Broadcasts logout events to all tabs
 * - Broadcasts token expiration events
 * - Broadcasts session timeout events
 * - Prevents race conditions with message deduplication
 * - Ensures consistent token removal across tabs
 */
@Injectable({
  providedIn: 'root'
})
export class CrossTabSessionService {
  private readonly CHANNEL_NAME = 'auth_sync_channel';
  private readonly STORAGE_KEY = 'auth_sync_message';
  private readonly MESSAGE_TIMEOUT = 5000; // 5 seconds to consider a message as duplicate
  
  // BroadcastChannel for modern browsers
  private broadcastChannel: BroadcastChannel | null = null;
  
  // Subject to emit auth events to subscribers
  private authEventSubject = new Subject<AuthSyncMessage>();
  public authEvent$ = this.authEventSubject.asObservable();
  
  // Track processed messages to prevent duplicates
  private processedMessages = new Map<string, number>();
  
  // Track if this tab initiated the logout to prevent processing own messages
  private isInitiator = false;

  // Store interval and listener references for cleanup
  private cleanupIntervalId: any;
  private storageEventListener: ((event: StorageEvent) => void) | null = null;

  constructor(private ngZone: NgZone) {
    this.initializeCommunication();
    this.startMessageCleanup();
  }

  /**
   * Initialize cross-tab communication using BroadcastChannel or localStorage events
   */
  private initializeCommunication(): void {
    // Try to use BroadcastChannel API (supported in modern browsers)
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.broadcastChannel = new BroadcastChannel(this.CHANNEL_NAME);
        this.broadcastChannel.onmessage = (event) => {
          this.handleMessage(event.data);
        };
        console.log('CrossTabSessionService: Using BroadcastChannel for cross-tab communication');
      } catch (error) {
        console.warn('CrossTabSessionService: BroadcastChannel not available, falling back to localStorage', error);
        this.broadcastChannel = null;
        this.setupStorageListener();
      }
    } else {
      // Fallback to localStorage events for older browsers
      console.log('CrossTabSessionService: Using localStorage events for cross-tab communication');
      this.setupStorageListener();
    }
  }

  /**
   * Setup localStorage event listener for cross-tab communication fallback
   */
  private setupStorageListener(): void {
    this.storageEventListener = (event: StorageEvent) => {
      // Only process our specific storage key
      if (event.key === this.STORAGE_KEY && event.newValue) {
        try {
          const message = JSON.parse(event.newValue) as AuthSyncMessage;
          // Run inside Angular zone when processing the message
          this.ngZone.run(() => {
            this.handleMessage(message);
          });
        } catch (error) {
          console.error('CrossTabSessionService: Error parsing storage event message', error);
        }
      }
    };
    
    // Run outside Angular zone to avoid unnecessary change detection
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('storage', this.storageEventListener!);
    });
  }

  /**
   * Handle incoming auth sync messages
   */
  private handleMessage(message: AuthSyncMessage): void {
    // Validate message structure
    if (!message || !message.type || !message.timestamp || !message.messageId) {
      console.warn('CrossTabSessionService: Invalid message received', message);
      return;
    }

    // Check if this message was already processed (deduplication)
    if (this.isDuplicateMessage(message)) {
      console.log('CrossTabSessionService: Duplicate message ignored', message.messageId);
      return;
    }

    // Mark message as processed
    this.markMessageAsProcessed(message);

    console.log('CrossTabSessionService: Processing auth event', message.type, 'from tab', message.messageId);

    // Emit the event to subscribers
    this.authEventSubject.next(message);
  }

  /**
   * Check if a message was already processed (prevents duplicate processing)
   */
  private isDuplicateMessage(message: AuthSyncMessage): boolean {
    const processedTime = this.processedMessages.get(message.messageId);
    if (processedTime) {
      const timeSinceProcessed = Date.now() - processedTime;
      return timeSinceProcessed < this.MESSAGE_TIMEOUT;
    }
    return false;
  }

  /**
   * Mark a message as processed with timestamp
   */
  private markMessageAsProcessed(message: AuthSyncMessage): void {
    this.processedMessages.set(message.messageId, Date.now());
  }

  /**
   * Broadcast logout event to all other tabs
   */
  public broadcastLogout(reason: string): void {
    const message: AuthSyncMessage = {
      type: 'logout',
      timestamp: Date.now(),
      messageId: this.generateMessageId(),
      data: { reason }
    };

    this.isInitiator = true;
    this.sendMessage(message);
    
    // Reset initiator flag after a short delay
    setTimeout(() => {
      this.isInitiator = false;
    }, 100);
  }

  /**
   * Broadcast token expiration event to all other tabs
   */
  public broadcastTokenExpired(): void {
    const message: AuthSyncMessage = {
      type: 'token_expired',
      timestamp: Date.now(),
      messageId: this.generateMessageId(),
      data: {}
    };

    this.sendMessage(message);
  }

  /**
   * Broadcast session timeout event to all other tabs
   */
  public broadcastSessionTimeout(): void {
    const message: AuthSyncMessage = {
      type: 'session_timeout',
      timestamp: Date.now(),
      messageId: this.generateMessageId(),
      data: {}
    };

    this.sendMessage(message);
  }

  /**
   * Send message via BroadcastChannel or localStorage
   */
  private sendMessage(message: AuthSyncMessage): void {
    // Mark our own message as processed to avoid self-handling
    this.markMessageAsProcessed(message);

    if (this.broadcastChannel) {
      // Use BroadcastChannel
      try {
        this.broadcastChannel.postMessage(message);
      } catch (error) {
        console.error('CrossTabSessionService: Error posting to BroadcastChannel', error);
        // Fallback to localStorage if BroadcastChannel fails
        this.sendViaLocalStorage(message);
      }
    } else {
      // Use localStorage events
      this.sendViaLocalStorage(message);
    }
  }

  /**
   * Send message via localStorage (for fallback or older browsers)
   */
  private sendViaLocalStorage(message: AuthSyncMessage): void {
    try {
      // Write to localStorage to trigger storage event in other tabs
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(message));
      
      // Clean up after a short delay to avoid cluttering localStorage
      setTimeout(() => {
        try {
          const currentValue = localStorage.getItem(this.STORAGE_KEY);
          if (currentValue) {
            const currentMessage = JSON.parse(currentValue);
            // Only remove if it's still our message
            if (currentMessage.messageId === message.messageId) {
              localStorage.removeItem(this.STORAGE_KEY);
            }
          }
        } catch (error) {
          console.error('CrossTabSessionService: Error cleaning up localStorage message', error);
        }
      }, 1000);
    } catch (error) {
      console.error('CrossTabSessionService: Error writing to localStorage', error);
    }
  }

  /**
   * Generate a unique message ID
   */
  private generateMessageId(): string {
    return `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Periodically clean up old processed messages to prevent memory leaks
   */
  private startMessageCleanup(): void {
    this.cleanupIntervalId = setInterval(() => {
      const now = Date.now();
      const messagesToDelete: string[] = [];

      this.processedMessages.forEach((timestamp, messageId) => {
        if (now - timestamp > this.MESSAGE_TIMEOUT * 2) {
          messagesToDelete.push(messageId);
        }
      });

      messagesToDelete.forEach(messageId => {
        this.processedMessages.delete(messageId);
      });

      if (messagesToDelete.length > 0) {
        console.log(`CrossTabSessionService: Cleaned up ${messagesToDelete.length} old messages`);
      }
    }, this.MESSAGE_TIMEOUT);
  }

  /**
   * Check if this tab is the initiator of the current action
   */
  public isCurrentTabInitiator(): boolean {
    return this.isInitiator;
  }

  /**
   * Cleanup when service is destroyed
   */
  public destroy(): void {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
    if (this.storageEventListener) {
      window.removeEventListener('storage', this.storageEventListener);
    }
    if (this.cleanupIntervalId) {
      clearInterval(this.cleanupIntervalId);
    }
    this.authEventSubject.complete();
    this.processedMessages.clear();
  }
}

/**
 * Auth sync message interface
 */
export interface AuthSyncMessage {
  type: 'logout' | 'token_expired' | 'session_timeout';
  timestamp: number;
  messageId: string;
  data: {
    reason?: string;
  };
}
